import type {
  IngestionStatus,
  NormalizationReport,
} from "~/ui-rtk/api/ingestionApi"

export const STATUS_COLORS: Record<IngestionStatus, string> = {
  PENDING: "bg-system-gray-500 text-white",
  VALIDATING: "bg-system-blue-500 text-white",
  VALIDATION_FAILED: "bg-system-error-500 text-white",
  PROCESSING: "bg-system-warning-500 text-white",
  COMPLETED: "bg-system-success-500 text-white",
  FAILED: "bg-system-error-500 text-white",
}

export const STATUS_LABELS: Record<IngestionStatus, string> = {
  PENDING: "Pending",
  VALIDATING: "Validating",
  VALIDATION_FAILED: "Validation Failed",
  PROCESSING: "Processing",
  COMPLETED: "Completed",
  FAILED: "Failed",
}

export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

const TERMINAL_STATUSES: ReadonlySet<IngestionStatus> =
  new Set<IngestionStatus>(["COMPLETED", "FAILED", "VALIDATION_FAILED"])

/** A file in a terminal state will not change again — stop polling. */
export const isTerminalStatus = (status: IngestionStatus): boolean =>
  TERMINAL_STATUSES.has(status)

/**
 * Turn a NormalizationReport into human-readable lines for the detail view:
 * `fixes` is what we auto-corrected, `warnings` is what still needs the
 * customer's attention (missing/mostly-empty columns).
 */
export const getNormalizationSummary = (
  report: NormalizationReport,
): { fixes: string[]; warnings: string[] } => {
  const fixes: string[] = []
  if (report.file_type === "xlsx") {
    fixes.push(
      report.sheet_name
        ? `Converted Excel (.xlsx) worksheet "${report.sheet_name}" to CSV`
        : "Converted Excel (.xlsx) to CSV",
    )
  }
  if (report.encoding_changed) {
    fixes.push(
      report.encoding
        ? `Re-encoded from ${report.encoding} to UTF-8`
        : "Re-encoded file to UTF-8",
    )
  }
  if (report.delimiter_changed) {
    let shown = "non-standard"
    if (report.delimiter) {
      shown = report.delimiter === "\t" ? "tab" : report.delimiter
    }
    fixes.push(`Detected "${shown}" as the column separator`)
  }
  if (report.header_rows_skipped > 0) {
    fixes.push(
      `Skipped ${report.header_rows_skipped} row${report.header_rows_skipped > 1 ? "s" : ""} above the header`,
    )
  }
  Object.entries(report.renamed_columns ?? {}).forEach(([from, to]) =>
    fixes.push(`Renamed "${from}" → "${to}"`),
  )
  if (report.blanks_filled_columns?.length) {
    fixes.push(
      `Filled blank cells with 0 in ${report.blanks_filled_columns.join(", ")}`,
    )
  }
  if (report.dropped_columns?.length) {
    fixes.push(
      `Dropped unrecognized column${report.dropped_columns.length > 1 ? "s" : ""}: ${report.dropped_columns.join(", ")}`,
    )
  }

  const warnings: string[] = []
  ;(report.missing_columns ?? []).forEach(column =>
    warnings.push(`Expected column "${column}" was not found in the file`),
  )
  ;(report.high_null_columns ?? []).forEach(({ column, null_rate: nullRate }) =>
    warnings.push(
      `"${column}" is ${Math.round(nullRate * 100)}% empty after parsing — check the source format`,
    ),
  )

  return { fixes, warnings }
}

/**
 * PUT a file to a presigned S3 URL (APP-537).
 *
 * Deliberately not RTK Query: its baseQuery prepends the API origin and sends
 * our credentials, and a presigned URL is signed over exactly the headers S3
 * will see — anything extra, or anything missing from `requiredHeaders`,
 * fails the signature. XMLHttpRequest rather than fetch because only XHR
 * reports upload progress, and a 100 MB upload with no progress looks frozen.
 */
export const uploadToPresignedUrl = (
  uploadUrl: string,
  file: File,
  requiredHeaders: Record<string, string>,
  onProgress?: (fraction: number) => void,
): Promise<void> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open("PUT", uploadUrl)
    Object.entries(requiredHeaders).forEach(([name, value]) =>
      xhr.setRequestHeader(name, value),
    )
    xhr.upload.onprogress = event => {
      if (event.lengthComputable) onProgress?.(event.loaded / event.total)
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        reject(new Error(`S3 rejected the upload (HTTP ${xhr.status})`))
      }
    }
    xhr.onerror = () =>
      reject(new Error("Network error while uploading to storage"))
    xhr.send(file)
  })
